import { fbt } from 'fbtee'
import { EyeOff, UserX } from 'lucide-react'
import { UserAvatar } from '../../../components/UserAvatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { cn } from '../../../lib/utils'
import type { ScheduleMarker } from '../../../types'
import { useScheduleMarkersContext } from './scheduleMarkersContext'

// The one tappable part of a schedule line, at the grid's left edge: small to
// the eye, with a padded hit area. The author's pin is a drag handle (a tap
// opens the editor); anyone else's shows the author and opens the hide menu.
export function ScheduleMarkerPin({
  marker,
  mine,
  dragging,
  onPointerDown,
}: {
  marker: ScheduleMarker
  mine: boolean
  dragging: boolean
  onPointerDown: (e: React.PointerEvent) => void
}) {
  const { profiles, hideLine, hideAuthor } = useScheduleMarkersContext()
  const author = profiles[marker.userId]
  const name = author?.displayName ?? ''

  // The author's own photo, with a grip drawn over it so it reads as a handle.
  if (mine) {
    return (
      <button
        type="button"
        data-testid="schedule-marker-pin"
        aria-label={fbt(
          'Line ' + fbt.param('label', marker.label) + ', drag to move or tap to edit',
          "Accessible label for the handle of the viewer's own custom schedule line",
        ).toString()}
        onPointerDown={onPointerDown}
        className="pointer-events-auto touch-none cursor-grab active:cursor-grabbing -m-2 p-2"
      >
        <span className={cn('relative block rounded-full ring-1 ring-foreground/80 transition-transform', dragging && 'scale-125')}>
          <UserAvatar profile={author} fallbackName={name} size="xs" className="size-5 text-[9px]" />
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-[2px] rounded-full bg-background/20">
            <span className="h-px w-2 rounded-full bg-foreground/90 shadow-[0_0_2px_var(--background)]" />
            <span className="h-px w-2 rounded-full bg-foreground/90 shadow-[0_0_2px_var(--background)]" />
          </span>
        </span>
      </button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="schedule-marker-pin"
          aria-label={fbt(
            'Line ' + fbt.param('label', marker.label) + ' by ' + fbt.param('name', name),
            'Accessible label for the pin of a custom schedule line someone else shared',
          ).toString()}
          className="pointer-events-auto cursor-pointer -m-2 p-2"
        >
          <UserAvatar profile={author} fallbackName={name} size="xs" className="size-5 text-[9px] ring-2 ring-background/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onSelect={() => hideLine(marker)}>
          <EyeOff />
          {fbt('Hide this line', 'Menu item that hides one custom schedule line someone shared')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => hideAuthor(marker.userId)}>
          <UserX />
          {fbt('Hide lines from ' + fbt.param('name', name), 'Menu item that hides every schedule line a person shares')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
